import { Box, Separator } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonChecklist } from 'components/common/checklist';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { TeamsContext } from 'contexts/admin/teams.context';
import { UsersContext } from 'contexts/admin/users.context';
import { IFullDialog } from 'interfaces/i-dialogs';
import {
  LoginDomain,
  USER_ROLES,
  UserRole,
  UserStatus,
} from 'lib_ts/enums/auth.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IUserPermissions } from 'lib_ts/interfaces/i-permissions';
import { IUser } from 'lib_ts/interfaces/i-user';
import { useContext, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'EditUserDialog';

const LOGIN_DOMAINS: LoginDomain[] = [
  LoginDomain.Local,
  LoginDomain.Dev,
  LoginDomain.Prod,
  LoginDomain.Staging,
];

const USER_STATUSES: UserStatus[] = [
  UserStatus.unverified,
  UserStatus.verified,
];

const USER_STATUS_OPTIONS: IOption[] = USER_STATUSES.map((s) => ({
  label: s,
  value: s,
}));

interface IProps {
  /** undefined means we're creating a new user */
  id?: string;
  onClose: () => void;
}

type SharedListParent = PitchListOwner.Team | PitchListOwner.Machine;

const SHARED_LIST_PARENTS: IOption[] = [
  {
    label: 'Team Lists',
    value: PitchListOwner.Team,
  },
  {
    label: 'Machine Lists',
    value: PitchListOwner.Machine,
  },
];

export const EditUserDialog = ({ id, onClose }: IProps) => {
  const teamsCx = useContext(TeamsContext);
  const usersCx = useContext(UsersContext);

  const current = useAuthStore((s) => s.current);
  const machines = useMachinesPortalStore((s) => s.machines);

  const initialUser = useMemo((): Partial<IUser> => {
    if (id) {
      /** updating an existing user */
      const found = usersCx.users.find((u) => u._id === id);

      if (!found) {
        throw new Error(`Could not find user with _id ${id}`);
      } else {
        return {
          ...found,
          password: '',
        };
      }
    } else {
      /** creating a new user, limit the domain to prod by default */
      return {
        domains: [LoginDomain.Prod],
        status: UserStatus.verified, // all new legacy users should be considered verified
      };
    }
  }, [id, usersCx.users]);

  const initialLists = useMemo(() => {
    const lists: SharedListParent[] = [];

    if (initialUser.permissions?.machine_lists) {
      lists.push(PitchListOwner.Machine);
    }

    if (initialUser.permissions?.team_lists) {
      lists.push(PitchListOwner.Team);
    }

    return lists;
  }, [initialUser.permissions]);

  const [user, setUser] = useState<Partial<IUser>>(initialUser);
  const [lists, setLists] = useState<SharedListParent[]>(initialLists);
  const [permissions] = useState<Partial<IUserPermissions>>(
    initialUser.permissions ?? {}
  );

  const roleOptions = useMemo(
    () =>
      current.role === UserRole.admin
        ? USER_ROLES
        : // team admins should not be able to make someone a super admin
          USER_ROLES.filter((m) => m.value !== UserRole.admin),
    [current.role]
  );

  const teamOptions = useMemo(
    () =>
      teamsCx.teams.map((t) => ({
        label: t.name,
        value: t._id,
      })),
    [teamsCx.teams]
  );

  const machineOptions = useMemo(
    () =>
      machines
        .filter(
          // prevent assigning users to machines of another team (only relevant for super admins)
          (m) => m._parent_id === user._parent_id
        )
        .filter(
          // prevent assigning users to deleted machines
          (m) => !m.deleted
        )
        .map((o) => ({
          label: MachineContextHelper.getMachineMenuLabel(o),
          value: o.machineID,
        })),
    [machines, user._parent_id]
  );

  const isNew = !user._id;

  const updateMetadata = (model: Partial<IUser>) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...model,
    }));
  };

  const content = (
    <CommonFormGrid columns={2}>
      <CommonTextInput
        id="user-username"
        label="Username"
        type="email"
        placeholder="email@domain.com"
        value={user.email}
        onChange={(v) => updateMetadata({ email: v })}
      />

      <CommonTextInput
        id="user-password"
        label="Password"
        placeholder="*********"
        onChange={(v) => updateMetadata({ password: v })}
        optional={!isNew}
      />

      <CommonSearchInput
        id="user-team"
        name="_parent_id"
        label="common.team"
        options={teamOptions}
        values={user._parent_id ? [user._parent_id] : []}
        onChange={(v) =>
          updateMetadata({
            _parent_id: v[0],
            _parent_def: PitchListOwner.Team,
            _parent_field: 'users',
          })
        }
        optional
      />
      <CommonSearchInput
        id="user-machineID"
        label="common.machine"
        options={machineOptions}
        values={user.machineID ? [user.machineID] : []}
        onChange={(machineIDs) =>
          updateMetadata({
            machineID: machineIDs[0],
          })
        }
      />
      <CommonSelectInput
        id="user-role"
        name="role"
        label="Role"
        options={roleOptions}
        value={user.role}
        onChange={(v) => updateMetadata({ role: v as UserRole })}
        optional
      />
      <CommonSelectInput
        id="user-status"
        name="status"
        label="Status"
        options={USER_STATUS_OPTIONS}
        value={user.status}
        onChange={(v) => updateMetadata({ status: v as UserStatus })}
        disabled={isNew} // all new legacy users should be verified
        optional
      />

      <Box gridColumn="span 2">
        <Separator size="4" />
      </Box>

      <CommonChecklist
        id="user-list-access"
        name="lists"
        label="Pitch List Access"
        values={lists}
        options={SHARED_LIST_PARENTS}
        onChange={(selected) => setLists(selected)}
      />

      <Box>
        Select one or more to grant the user access to shared pitch lists
        (personal lists are always enabled).
      </Box>

      {current.role === UserRole.admin && (
        <>
          <Box gridColumn="span 2">
            <Separator size="4" />
          </Box>

          <Box>
            <CommonChecklist
              id="user-domains"
              label="Login Domains"
              labelIcon={<SuperAdminIcon />}
              name="domains"
              values={user.domains ?? []}
              options={LOGIN_DOMAINS.map((d) => ({
                label: d === 'trajektsports.app' ? 'prod' : d.split('.')[0],
                value: d,
              }))}
              onChange={(selected) => {
                setUser((prevUser) => ({
                  ...prevUser,
                  domains: selected,
                }));
              }}
            />
          </Box>

          <Box>
            Select one or more Login Domains above to limit the user's login
            access to the selected environments. If no domains are selected, the
            user will have access to all environments.
          </Box>
        </>
      )}
    </CommonFormGrid>
  );

  const onSave = async () => {
    if (!user._parent_id) {
      NotifyHelper.error({
        message_md: 'Please select a team and try again.',
      });
      return;
    }

    if (!user.machineID) {
      NotifyHelper.error({
        message_md: 'Please select a machine and try again.',
      });
      return;
    }

    if (!user.role) {
      NotifyHelper.error({
        message_md: 'Please select a role and try again.',
      });
      return;
    }

    if (!user.status) {
      NotifyHelper.error({
        message_md: 'Please select a status and try again.',
      });
      return;
    }

    /** convert selected shared list parents into enable flags */
    const perms: Partial<IUserPermissions> = {
      ...permissions,
      team_lists: lists.includes(PitchListOwner.Team),
      machine_lists: lists.includes(PitchListOwner.Machine),
    };

    const payload: Partial<IUser> = {
      ...user,
      permissions: perms,
    };

    if (isNew) {
      if (!payload.password) {
        NotifyHelper.error({
          message_md: 'Please provide a password and try again.',
        });
        return;
      }

      try {
        await usersCx.createUser(payload);
        onClose();
      } catch (error) {
        NotifyHelper.error({
          message_md: `Failed to create user: ${error}`,
        });
      }

      return;
    }

    try {
      await usersCx.updateUser(payload);
      onClose();
    } catch (error) {
      NotifyHelper.error({
        message_md: `Failed to update user: ${error}`,
      });
    }
  };

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.MD,
    loading: usersCx.loading,
    title: isNew ? 'Create User' : 'Update User',
    content,
    buttons: [
      {
        label: isNew ? 'Create' : 'Update',
        color: RADIX.COLOR.ACCENT,
        variant: 'solid',
        onClick: onSave,
      },
    ],
    onClose: onClose,
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};
